import * as Yup from "yup";
import {AnyObject, type Maybe, Message} from "yup/lib/types";
import Reference from "yup/lib/Reference";
import {DateUtils, DateUtilsTypes} from "../../../core";
import BaseSchema from "yup/lib/schema";


declare module "yup" {

    export interface StringSchema<TType extends Maybe<string> = string | undefined, TContext extends AnyObject = AnyObject, TOut extends TType = TType> extends Yup.BaseSchema<TType, TContext, TOut> {

        phoneNo(message?: Message, validLength?: number): StringSchema<TType, TContext, TOut>;

        emailAddress(message?: Message, required?: boolean): StringSchema<TType, TContext, TOut>;
    }

    // @ts-ignore
    export class MixedSchema<TType = any, TContext = AnyObject, TOut = TType> extends Yup.BaseSchema<TType, TContext, TOut> {

        validDate(message?: Message, required?: boolean): MixedSchema<TType, TContext, TOut>;

        minDate(minDate: unknown | Reference<DateUtilsTypes.Instance> | (() => unknown), message?: Message, unit?: DateUtilsTypes.OpUnitType): MixedSchema<TType, TContext, TOut>;

        maxDate(maxDate: unknown | Reference<DateUtilsTypes.Instance> | (() => unknown), message?: Message, unit?: DateUtilsTypes.OpUnitType): MixedSchema<TType, TContext, TOut>;

        disableFutureDate(message?: Message, unit?: DateUtilsTypes.OpUnitType): MixedSchema<TType, TContext, TOut>;

        disablePastDate(message?: Message, unit?: DateUtilsTypes.OpUnitType): MixedSchema<TType, TContext, TOut>;
    }

    export interface NumberSchema<TType extends Maybe<number> = number | undefined, TContext extends AnyObject = AnyObject, TOut extends TType = TType> extends BaseSchema<TType, TContext, TOut> {

        maxNumber(max: number | Reference<unknown>, message?: Message): NumberSchema<TType, TContext, TOut>;

        minNumber(min: number | Reference<unknown>, message?: Message): NumberSchema<TType, TContext, TOut>;
    }
}

/**
 * Houses all the customized test functions added to different types of the Yup package.
 *
 * - NOTE: be sure to add each of the necessary extensions in the files that you intend to use them.
 */
class YupExtensions {

    /**
     * Adds the phone number method to Yup String types.
     */
    public static phoneNo(): void {
        Yup.addMethod(Yup.string, 'phoneNo', function (...args) {
            const [message, validLength = 12] = args;
            return this.test('test-phone-no', message, function (val) {
                let value = val?.trim() ?? "";
                if (!val) {
                    return true;
                }
                value = value
                    .replace(/[()_-]/g, '')
                    // .replace(/\+1/g, '')
                    .replaceAll(' ', '');
                if (!val) {
                    return true;
                }
                return value?.length === validLength;
            });
        });
    }

    /**
     * Adds the validDate method to Yup Mixed types that validates a date.
     */
    public static validDate(): void {
        Yup.addMethod(Yup.mixed, 'validDate', function (...args) {
            const [message, required = false] = args;
            return this.test({
                name: 'test-valid-date',
                message,
                exclusive: true,
                params: {
                    required,
                },
                test: (val) => {
                    if (!val) {
                        return !required;
                    }
                    if (!DateUtils.instance.isDayjs(val)) {
                        return false;
                    }
                    return val.isValid();
                }
            });
        });
    }


    /**
     * Adds the minDate method to Yup Mixed types that validates a date.
     */
    public static minDate(): void {
        Yup.addMethod(Yup.mixed, 'minDate', function (...args) {
            const [minDate, message, unit] = args;
            const prepareParam = (ref: any) => {
                let param: any;
                if (!Reference.isRef(ref)) {
                    if (typeof ref === 'function') {
                        ref = ref();
                    }
                    param = this.cast(ref);
                } else {
                    param = ref;
                }
                return param;
            }
            const limit = prepareParam(minDate);
            return this.test({
                name: 'test-min-date',
                message: message,
                exclusive: true,
                params: {
                    min: limit,
                },
                test: function (val) {
                    if (!val || !DateUtils.instance.isDayjs(val) || !DateUtils.UTC(val).isValid()) {
                        return true;
                    }
                    const minDateResolved = this.resolve(limit) as any;
                    if (!minDateResolved)
                        return true;
                    return val.isSameOrAfter(minDateResolved, unit);
                }
            });
        });
    }

    /**
     * Adds the maxDate method to Yup Mixed types that validates a date.
     */
    public static maxDate(): void {
        Yup.addMethod(Yup.mixed, 'maxDate', function (...args) {
            const [maxDate, message, unit] = args;

            const prepareParam = (ref: any) => {
                let param: any;
                if (!Reference.isRef(ref)) {
                    if (typeof ref === 'function') {
                        ref = ref();
                    }
                    param = this.cast(ref);
                } else {
                    param = ref;
                }
                return param;
            }
            const limit = prepareParam(maxDate);
            return this.test({
                name: 'test-max-date',
                message: message,
                exclusive: true,
                params: {
                    max: limit,
                },
                test: function (val) {
                    if (!val || !DateUtils.instance.isDayjs(val) || !DateUtils.UTC(val).isValid()) {
                        return true;
                    }
                    const maxDateResolved = this.resolve(limit) as any;
                    if (!maxDateResolved)
                        return true;
                    return val.isSameOrBefore(maxDateResolved, unit);
                }
            });
        });
    }

    /**
     * Adds the disableFutureDate method to Yup Mixed types that validates a date.
     */
    public static disableFutureDate(): void {
        Yup.addMethod(Yup.mixed, 'disableFutureDate', function (...args) {
            const [message, unit] = args;
            return this.test({
                name: 'test-disable-future-date',
                message,
                exclusive: true,
                test: function (val) {
                    if (!val || !DateUtils.instance.isDayjs(val) || !DateUtils.UTC(val).isValid()) {
                        return true;
                    }
                    return val.isSameOrBefore(DateUtils.localNowToUTC(), unit);
                }
            });
        });
    }

    /**
     * Adds the disablePastDate method to Yup Mixed types that validates a date.
     */
    public static disablePastDate(): void {
        Yup.addMethod(Yup.mixed, 'disablePastDate', function (...args) {
            const [message, unit] = args;
            return this.test({
                name: 'test-disable-past-date',
                message,
                exclusive: true,
                test: function (val) {
                    if (!val || !DateUtils.instance.isDayjs(val) || !DateUtils.UTC(val).isValid()) {
                        return true;
                    }
                    return val.isSameOrAfter(DateUtils.localNowToUTC(), unit);
                }
            });
        });
    }


    /**
     * Adds the numberMax method to Yup Number types.
     */
    public static maxNumber(): void {
        Yup.addMethod(Yup.number, 'maxNumber', function (...args) {
            const [max, message] = args;
            const prepareParam = (ref: any) => {
                let param: any;
                if (!Reference.isRef(ref)) {
                    if (typeof ref === 'function') {
                        ref = ref();
                    }
                    param = this.cast(ref);
                } else {
                    param = ref;
                }
                return param;
            }
            const limit = prepareParam(max);
            return this.test({
                name: 'test-number-max',
                message: message,
                exclusive: true,
                params: {
                    max: limit,
                },
                test: function (val) {
                    const resolvedLimit = this.resolve(limit) as any;
                    if ((val === undefined || val === null) || (resolvedLimit === undefined || resolvedLimit === null)) {
                        return true;
                    }
                    return val <= resolvedLimit;
                }
            });
        });
    }

    /**
     * Adds the minNumber method to Yup Number types.
     */
    public static minNumber(): void {
        Yup.addMethod(Yup.number, 'minNumber', function (...args) {
            const [min, message] = args;
            const prepareParam = (ref: any) => {
                let param: any;
                if (!Reference.isRef(ref)) {
                    if (typeof ref === 'function') {
                        ref = ref();
                    }
                    param = this.cast(ref);
                } else {
                    param = ref;
                }
                return param;
            }
            const limit = prepareParam(min);
            return this.test({
                name: 'test-number-min',
                message: message,
                exclusive: true,
                params: {
                    min: limit,
                },
                test: function (val) {
                    const resolvedLimit = this.resolve(limit) as any;
                    if ((val === undefined || val === null) || (resolvedLimit === undefined || resolvedLimit === null)) {
                        return true;
                    }
                    return val >= resolvedLimit;
                }
            });
        });
    }


    /**
     * Injects all the extensions of this interface to yup.
     */
    public static injectExtensions(): void {
        this.phoneNo();
        this.validDate();
        this.minDate();
        this.maxDate();
        this.disablePastDate();
        this.disableFutureDate();
        this.maxNumber();
        this.minNumber();
    }
}

export * as Yup from "yup";
export default YupExtensions;
